import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import {
	AppBar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Toolbar,
	Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { addDays, startOfToday } from 'date-fns';
import { DialogTransition, FollowUpHistory } from 'components';
import { animated, useTransition } from '@react-spring/web';
import { useScreenType } from 'hooks';
import { EActionState, EFollowUpType, EOpportunityFollowUpStatus } from 'models';
import { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreator, selectors } from 'store/followups/create';
import { dateService } from 'services';
import { theme } from 'theme';
import { pacsService } from 'services';
import { messageFactory } from 'store/messaging';

export const CreateFollowUpDialog = () => {
	const dispatch = useDispatch();
	const loading = useSelector(selectors.getInitAction);
	const open = useSelector(selectors.getOpen);
	const screenType = useScreenType();

	// Form state from Redux
	const type = useSelector(selectors.form.getType);
	const date = useSelector(selectors.form.getDate);
	const time = useSelector(selectors.form.getTime);
	const length = useSelector(selectors.form.getLength);
	const specialInstructions = useSelector(selectors.form.getSpecialInstructions);

	const handleCancel = () => {
		dispatch(actionCreator.INIT_FLUSH());
	};

	// Basic required validation for all fields
	const fieldErrors = {
		type: type.newValue.length === 0,
		date: date.newValue.length === 0,
		time: time.newValue.length === 0,
		length: length.newValue.length === 0,
		specialInstructions: specialInstructions.newValue.trim().length === 0 && pacsService.FollowUpCreateNotesRequired
	};
	const isFormValid = !(fieldErrors.type || fieldErrors.date || fieldErrors.time || fieldErrors.length || fieldErrors.specialInstructions);

	return (
		<Dialog
			open={open}
			fullScreen={screenType.isPhone}
			onClose={(event, reason) => {
				if (reason === 'backdropClick') return;
				handleCancel();
			}}
			fullWidth={true}
			maxWidth={'md'}
			TransitionComponent={DialogTransition}
			transitionDuration={{
				appear: theme.transitions.duration.enteringScreen,
				enter: theme.transitions.duration.enteringScreen,
				exit: 0
			}}>
			{screenType.isPhone && <TitleBar title='Create Follow Up' onClose={handleCancel} />}
			{screenType.isDesktop && <DialogTitle>Create Follow Up</DialogTitle>}
			<DialogContent sx={{ p: 0, minHeight: 420, overflow: 'hidden' }}>
				{loading === EActionState.RUNNING && (
					<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
						<CircularProgress />
					</Box>
				)}
				{(() => {
					const show = loading === EActionState.COMPLETED;
					const transitions = useTransition(show, {
						from: { opacity: 0, transform: 'translateY(6px)' },
						enter: { opacity: 1, transform: 'translateY(0px)' },
						leave: { opacity: 0, transform: 'translateY(6px)' },
						config: { tension: 200, friction: 20 }
					});
					return transitions(
						(styles, item) =>
							item && (
								<animated.div style={{ ...styles, willChange: 'opacity, transform', display: 'block' }}>
									<Box sx={{ width: '100%', p: 2 }}>
										<Content fieldErrors={fieldErrors} />
									</Box>
								</animated.div>
							)
					);
				})()}
			</DialogContent>
			<DialogActions>
				<SaveButton isFormValid={isFormValid} />
				<Button color='neutral' variant='outlined' onClick={handleCancel} sx={{ flex: screenType.isPhone ? '1 1 0px' : 'inherit' }}>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
};

interface SaveButtonProps {
	isFormValid: boolean;
}

const SaveButton = (props: SaveButtonProps) => {
	const { isFormValid } = props;
	const dispatch = useDispatch();
	const createAction = useSelector(selectors.getCreateAction);
	const opportunityFollowUpStatus = useSelector(selectors.getOpportunityFollowUpStatus);
	const screenType = useScreenType();
	const handleClick = () => {
		if (opportunityFollowUpStatus === EOpportunityFollowUpStatus.CLOSED || opportunityFollowUpStatus === EOpportunityFollowUpStatus.CONSULTANT_COMPLETED) {
			dispatch(
				messageFactory.dialog(
					'Reopen Opportunity',
					'This opportunity is closed. Creating a follow up will reopen the opportunity. Do you want to continue?',
					actionCreator.CREATE_START(),
					null,
					true
				)
			);
		} else {
			dispatch(actionCreator.CREATE_START());
		}
	};
	return (
		<LoadingButton
			color='primary'
			variant='contained'
			onClick={handleClick}
			disabled={!isFormValid}
			loading={createAction === EActionState.RUNNING}
			sx={{ flex: screenType.isPhone ? '1 1 0px' : 'inherit' }}>
			Save
		</LoadingButton>
	);
};

const TitleBar = (props: { title: string; onClose: () => void }) => {
	const { title, onClose } = props;
	return (
		<AppBar position='static' color='inherit'>
			<Toolbar>
				<IconButton color='inherit' onClick={onClose}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
					{title}
				</Typography>
			</Toolbar>
		</AppBar>
	);
};

interface ContentProps {
	fieldErrors: {
		type: boolean;
		date: boolean;
		time: boolean;
		length: boolean;
		specialInstructions: boolean;
	};
}

const Content = (props: ContentProps) => {
	const { fieldErrors } = props;
	const dispatch = useDispatch();
	const type = useSelector(selectors.form.getType);
	const date = useSelector(selectors.form.getDate);
	const time = useSelector(selectors.form.getTime);
	const length = useSelector(selectors.form.getLength);
	const specialInstructions = useSelector(selectors.form.getSpecialInstructions);
	const inPersonStartTimes = useSelector(selectors.getInPersonStartTimes);
	const phoneStartTimes = useSelector(selectors.getPhoneStartTimes);
	const durations = useSelector(selectors.getDurations);
	const followUps = useSelector(selectors.getFollowUps);

	const timeWindows = type.newValue === EFollowUpType.IN_PERSON ? inPersonStartTimes : type.newValue === EFollowUpType.PHONE ? phoneStartTimes : [];
	const handleAppointmentTypeChange = (event: SelectChangeEvent) => {
		dispatch(actionCreator.SET_TYPE(event.target.value));
		if (event.target.value === EFollowUpType.IN_PERSON && !pacsService.FollowUpCreateAppointmentLengthEnabled) {
			dispatch(actionCreator.SET_LENGTH('02:00:00')); // in-person visits must be 2 hours to ensure PR works correctly
		}
	};

	const handleDateChange = (value: Date | null) => {
		dispatch(actionCreator.SET_DATE(value ? dateService.parseDateToString(value) : ''));
		dispatch(actionCreator.SET_TIME('')); // Reset time when date changes
	};

	const handleTimeChange = (event: SelectChangeEvent) => {
		dispatch(actionCreator.SET_TIME(event.target.value));
	};

	const handleLengthChange = (event: SelectChangeEvent) => {
		dispatch(actionCreator.SET_LENGTH(event.target.value));
	};

	const handleInstructionsChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const value = event.target.value;
		if (value.length <= 500) {
			dispatch(actionCreator.SET_SPECIAL_INSTRUCTIONS(value));
		}
	};

	return (
		<Box sx={{ p: 2, pt: 0 }}>
			<FollowUpHistory items={followUps} sx={{ pb: 3 }} />
			<Grid container spacing={2}>
				{/* Left Column - Form Fields */}
				<Grid item xs={12} md={6}>
					<Stack spacing={2}>
						{/* Appointment Type */}
						<FormControl fullWidth error={fieldErrors.type}>
							<InputLabel id='appointment-type-label'>Appointment Type</InputLabel>
							<Select labelId='appointment-type-label' value={type.newValue} label='Appointment Type' onChange={handleAppointmentTypeChange}>
								<MenuItem value={EFollowUpType.IN_PERSON}>{EFollowUpType.IN_PERSON}</MenuItem>
								<MenuItem value={EFollowUpType.PHONE}>{EFollowUpType.PHONE}</MenuItem>
							</Select>
							{fieldErrors.type && <FormHelperText>Required</FormHelperText>}
						</FormControl>

						{/* Appointment Date */}
						<DatePicker
							label='Appointment Date'
							value={date.newValue ? dateService.parseStringToDate(date.newValue) : null}
							onChange={handleDateChange}
							minDate={startOfToday()}
							maxDate={addDays(startOfToday(), pacsService.FollowUpCreateMaxDaysOut)}
							slotProps={{
								textField: {
									fullWidth: true,
									error: fieldErrors.date,
									helperText: fieldErrors.date ? 'Required' : ''
								}
							}}
						/>

						{/* Appointment Start Time */}
						<FormControl fullWidth error={fieldErrors.time}>
							<InputLabel id='appointment-time-label'>Appointment Start Time</InputLabel>
							<Select
								labelId='appointment-time-label'
								value={time.newValue}
								label='Appointment Start Time'
								onChange={handleTimeChange}
								disabled={timeWindows.length === 0}>
								{timeWindows.map(time => (
									<MenuItem key={time.value} value={time.value}>
										{time.displayText}
									</MenuItem>
								))}
							</Select>
							{fieldErrors.time && <FormHelperText>Required</FormHelperText>}
						</FormControl>

						{/* Appointment Length */}
						<FormControl fullWidth error={fieldErrors.length}>
							<InputLabel id='appointment-length-label'>Appointment Length</InputLabel>
							<Select
								labelId='appointment-length-label'
								value={length.newValue}
								label='Appointment Length'
								onChange={handleLengthChange}
								disabled={type.newValue === EFollowUpType.IN_PERSON && !pacsService.FollowUpCreateAppointmentLengthEnabled}>
								{durations.map(length => (
									<MenuItem key={length} value={length}>
										{length}
									</MenuItem>
								))}
							</Select>
							{fieldErrors.length && <FormHelperText>Required</FormHelperText>}
						</FormControl>
					</Stack>
				</Grid>

				{/* Right Column - Special Instructions */}
				<Grid item xs={12} md={6}>
					<Stack spacing={1}>
						<TextField
							label='Follow-up Instructions'
							multiline
							rows={8}
							fullWidth
							value={specialInstructions.newValue}
							onChange={handleInstructionsChange}
							inputProps={{ maxLength: 250 }}
							error={fieldErrors.specialInstructions}
							helperText={fieldErrors.specialInstructions ? 'Required' : ''}
						/>
						<Typography variant='caption' sx={{ textAlign: 'right', color: 'text.secondary' }}>
							{specialInstructions.newValue.length} / 250
						</Typography>
					</Stack>
				</Grid>
			</Grid>
		</Box>
	);
};
